import formatName from 'I18n/utils/formatName';

import { hasProperty } from 'marketing-platform-lib/utils/crmUtils';
import {
  CrmObject,
  Property,
  ObjectTypeDefinition,
} from 'framework-listing-lib/types';

/**
 * Returns the value of @propertyName in @crmObject.
 *
 * If @propertyName is not present:
 *   - returns undefined when @defaultValue is not provided.
 *   - returns @defaultValue otherwise.
 * @param crmObject The CRM object
 * @param propertyName The property name
 * @param defaultValue Optional default value if @propertyName is not found in @crmObject
 */
export function getPropertyValue(
  crmObject: CrmObject,
  propertyName: string,
  defaultValue?: Property['value']
) {
  if (hasProperty(crmObject, propertyName)) {
    return crmObject.properties[propertyName].value;
  }

  return defaultValue;
}

/**
 * Set property value and return a new CrmObject
 * @param crmObject The CRM Object.
 * @param propertyName Property name to be updated.
 * @param value Property value.
 */
export function setPropertyValue(
  crmObject: CrmObject,
  propertyName: string,
  value: Property['value']
): CrmObject {
  let result = {
    ...crmObject,
  };

  // Create property if it doesn't exist.
  // TODO: cast to any until we fully migrate the types to marketing-platform-lib
  if (!hasProperty(result as any, propertyName)) {
    result = {
      ...result,
      properties: {
        ...result.properties,
        [propertyName]: {
          value: '',
        },
      },
    };
  }

  result = {
    ...result,
    properties: {
      ...result.properties,
      [propertyName]: {
        ...result.properties[propertyName],
        value,
      },
    },
  };

  return result;
}

type CrmObjectLike = {
  objectTypeId: string;
  properties: Record<string, { value?: string | null } | undefined>;
};

export function getObjectLabel(
  crmObject: CrmObjectLike,
  objectTypeDefinition: ObjectTypeDefinition
) {
  const getPropertyValueLoose = (name: string) =>
    crmObject.properties[name]?.value;

  if (crmObject.objectTypeId === '0-1') {
    return formatName({
      firstName: getPropertyValueLoose('firstname'),
      lastName: getPropertyValueLoose('lastname'),
      email: getPropertyValueLoose('email'),
    });
  }

  return getPropertyValueLoose(
    objectTypeDefinition.primaryDisplayLabelPropertyName || 'hs_name'
  );
}

/**
 * Batch updates properties on a crmObject
 * @param crmObject The object to update. Will not be mutated, but also will not be entirely cloned
 * @param updates A map of property names to their new values
 * @returns A new crmObject with the property updates
 */
export function setPropertyValues(
  crmObject: CrmObject,
  updates: Record<string, Property['value']>
) {
  const draftProperties = { ...crmObject.properties };
  Object.entries(updates).forEach(([propertyName, value]) => {
    if (!draftProperties[propertyName]) {
      draftProperties[propertyName] = { value };
    } else if (draftProperties[propertyName].value !== value) {
      // Avoid mutating the original object
      draftProperties[propertyName] = {
        ...draftProperties[propertyName],
        value,
      };
    }
  });

  return {
    ...crmObject,
    properties: draftProperties,
  };
}
